import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';

import { FwProgressBarColor, FwProgressBarStatusColor } from '../../../shared/enums/fw-progress-bar.type';
import { AddDataProgress, OperationProgressTotals, XferOperationStatus } from '../../../shared/models/xfer.model';
import { FormatTimePipe } from '../../../shared/pipes/format-time-pipe.pipe';
import { TranslatePipe } from '../../../translation.service';

@Component({
  host: { 'test-id': 'operation-table-with-progress' },
  selector: 'operation-table-with-progress',
  styleUrls: ['./operation-table-with-progress.component.scss'],
  templateUrl: './operation-table-with-progress.component.html',
  providers: [TranslatePipe, FormatTimePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OperationTableWithProgress implements OnChanges {
  @Input() progress: AddDataProgress;
  @Input() progressBarColor: FwProgressBarColor = FwProgressBarColor.Primary;
  @Input() progressBarStatusText = '';
  @Input() progressBarStatusColor: FwProgressBarStatusColor;

  protected progressTitle = '';
  protected progressSubtitle = '';
  protected progressSummaryColor: FwProgressBarStatusColor;
  protected progressSummary = '';
  protected progressValue: number;

  protected readonly status = XferOperationStatus;

  protected title = '';
  protected isFinished = false;

  constructor(private translatePipe: TranslatePipe, private formatTimePipe: FormatTimePipe,) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.progress?.currentValue) {
      this.updatedIsFinished(this.progress);

      if (this.progress?.status2 !== this.status.Canceled && this.progress?.status2 !== this.status.Cancelled) {
        this.updateProgressValue(this.progress.totals);
      }

      this.updatedProgressSummary(this.progress);
      this.updatedProgressTitle(this.progress);
    }
  }

  private updateProgressValue(totals: OperationProgressTotals): void {
    this.progressValue = Math.round((totals.processed_bytes / totals.discovered_bytes) * 100);
  }

  private updatedIsFinished(progress: AddDataProgress): void {
    this.isFinished = progress.status2 === this.status.Cancelled || progress.status2 === this.status.Failed || progress.status2 === this.status.Completed;
  }

  private updatedProgressSummary(progress: AddDataProgress): void {
    this.progressSummary = '';
    this.progressSummaryColor = null;

    if (!this.isFinished) {
      return;
    }

    if (progress.status2 === this.status.Cancelling || progress.status2 === this.status.Canceling) {
      this.progressSummary = 'operationsTableWithProgress.status.cancelling';
      this.progressSummaryColor = FwProgressBarStatusColor.Grey;
    } else if (progress.status2 === this.status.Cancelled || progress.status2 === this.status.Canceled) {
      this.progressSummary = 'operationsTableWithProgress.status.cancelled';
      this.progressSummaryColor = FwProgressBarStatusColor.Grey;
    } else if (progress.status2 === this.status.Failed) {
      this.progressSummary = 'operationsTableWithProgress.status.failed';
      this.progressSummaryColor = FwProgressBarStatusColor.Red;
    } else if (progress.totals.skipped_files + progress.totals.failed_files === progress.totals.discovered_files && progress.totals.discovered_files) {
      const isPlural = progress.totals.discovered_files > 1;
      this.progressSummary = isPlural ? 'operationsTableWithProgress.status.noFilesTransferred' : 'operationsTableWithProgress.status.noFileTransferred';
      this.progressSummaryColor = FwProgressBarStatusColor.Red;
    } else if (progress.totals.failed_files + progress.totals.skipped_files === progress.totals.discovered_files) {
      this.progressSummary = 'operationsTableWithProgress.status.exceptionsFound';
      this.progressSummaryColor = FwProgressBarStatusColor.Red;
    }
    else if (progress.totals.failed_files) {
      const isPlural = progress.totals.failed_files > 1;
      this.progressSummary = isPlural ? 'operationsTableWithProgress.status.errorsFound' : 'operationsTableWithProgress.status.errorFound';
      this.progressSummaryColor = FwProgressBarStatusColor.Red;
    } else if (progress.totals.skipped_files) {
      const isPlural = progress.totals.skipped_files > 1;
      this.progressSummary = isPlural ? 'operationsTableWithProgress.status.skippedFiles' : 'operationsTableWithProgress.status.skippedFile';
      this.progressSummaryColor = FwProgressBarStatusColor.Orange;
    } else if (progress.status2 === this.status.Completed) {
      this.progressSummary = 'operationsTableWithProgress.status.complete';
      this.progressSummaryColor = FwProgressBarStatusColor.Green;
    }
  }

  private updatedProgressTitle(progress: AddDataProgress): void {
    this.progressTitle = '';
    const isPlural = progress.totals.discovered_files !== 1;

    if (progress.status2 === this.status.Pending) {
      this.progressTitle = isPlural ? 'operationsTableWithProgress.filesWaiting' : 'operationsTableWithProgress.fileWaiting';
      this.progressSubtitle = this.translatePipe.transform('operationsTableWithProgress.waiting');
      return;
    }

    if (progress.status2 === this.status.Cancelling || progress.status2 === this.status.Canceling || progress.status2 === this.status.Cancelled || progress.status2 === this.status.Canceled) {
      this.progressTitle = isPlural ? 'operationsTableWithProgress.filesProcessed' : 'operationsTableWithProgress.fileProcessed';
      this.progressSubtitle = this.translatePipe.transform('operationsTableWithProgress.fileCanceledSubtitle') + ' · ' +
        this.updateTimeValue(progress.elapsed_time, progress.remaining_time);
      return;
    }

    if (!this.isFinished) {
      this.progressTitle = progress.totals.processed_files === 1 ? 'operationsTableWithProgress.fileProcessed' : 'operationsTableWithProgress.filesProcessed';
      this.progressSubtitle = this.translatePipe.transform('operationsTableWithProgress.processed');
    } else {
      this.progressTitle = isPlural ? 'operationsTableWithProgress.filesProcessed' : 'operationsTableWithProgress.fileProcessed';
      this.progressSubtitle = this.translatePipe.transform('operationsTableWithProgress.processed');
      this.progressSubtitle += ' · ' + this.updateTimeValue(progress.elapsed_time, 0);
      return;
    }

    this.progressSubtitle += ' · ' + this.updateTimeValue(progress.elapsed_time, progress.remaining_time);
  }

  private updateTimeValue(elapsedTime: number, remainingTime: number): string {
    const transformedElapsedTime = this.formatTimePipe.transform(elapsedTime * 1000, this.translatePipe.transform('operationsTableWithProgress.elapsed'));

    if (!remainingTime || this.isFinished) {
      return transformedElapsedTime;
    }

    const transformedRemainingTime = this.formatTimePipe.transform(remainingTime * 1000, this.translatePipe.transform('operationsTableWithProgress.remaining'));

    if (!transformedRemainingTime) {
      return transformedElapsedTime;
    }

    return `${transformedElapsedTime} - ${transformedRemainingTime}`;
  }
}
